import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

export function useNormalizedSearchParams() {
    const [searchParams, setSearchParams] = useSearchParams();

    // Normalize URL parameters on mount and when they change
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let hasChanges = false;

        // Define parameter mappings (original case -> normalized case)
        const paramMappings = {
            'jobid': 'jobId',
            'userid': 'userId',
            'projectid': 'projectId',
            // Add more mappings as needed
        };

        // Check and normalize parameters
        for (const [key, value] of params.entries()) {
            const lowerKey = key.toLowerCase();
            const normalizedKey = paramMappings[lowerKey];

            if (normalizedKey && key !== normalizedKey) {
                // Remove the old parameter and add the normalized one
                params.delete(key);
                params.set(normalizedKey, value);
                hasChanges = true;
            }
        }

        // Update URL if changes were made
        if (hasChanges) {
            setSearchParams(params, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Wrap the original searchParams with lowercase key lookup
    const normalizedParams = new Proxy(searchParams, {
        get(target, prop) {
            if (prop === "get") {
                return (key) => {
                    // First try the exact key
                    if (target.has(key)) {
                        return target.get(key);
                    }
                    // Then try case-insensitive lookup
                    for (const [paramKey, paramValue] of target.entries()) {
                        if (paramKey.toLowerCase() === key.toLowerCase()) {
                            return paramValue;
                        }
                    }
                    return null;
                };
            }
            if (prop === "has") {
                return (key) => {
                    // First try the exact key
                    if (target.has(key)) {
                        return true;
                    }
                    // Then try case-insensitive lookup
                    for (const paramKey of target.keys()) {
                        if (paramKey.toLowerCase() === key.toLowerCase()) {
                            return true;
                        }
                    }
                    return false;
                };
            }
            return target[prop];
        },
    });

    return [normalizedParams, setSearchParams];
}